import axiosServices from 'utils/axios';
import useSWR, { mutate } from 'swr';
import { useMemo } from 'react';

import { RoleType } from 'api/roles';
import { tenantScopedKey } from 'utils/tenantCacheKey';
import { DEFAULT_USER_DATE_PREFERENCES, type UserDatePreferences } from 'utils/dateFormatter';

// ==============================|| USERS API ||============================== //

export interface CurrentUser {
  userId: number;
  defaultTenantId: number;
  hasPin: boolean;
  name: string;
  username: string;
  preferences: UserDatePreferences;
}

export interface UpdateCurrentUserPreferencesRequest extends UserDatePreferences {}

export interface CurrentUserPermissions {
  roleName: string | null;
  roleLevel: number | null;
  // Every role held at the level the roles resolved from, most privileged first.
  roleNames: string[];
  permissionCodes: string[];
}

const endpoints = {
  me: 'api/users/me',
  currentUserPreferences: 'api/users/me/preferences',
  permissions: 'api/users/me/permissions',
  search: 'api/users/search',
  directory: 'api/users/directory'
};

// Built with the shared helper so the tenant-change sweep recognises and clears it.
const currentUserCacheKey = (tenantId: number | null, userId?: string | null) =>
  userId ? tenantScopedKey(`${endpoints.me}:user:${userId}`, tenantId) : null;

export async function getCurrentUser(): Promise<CurrentUser> {
  const response = await axiosServices.get<Partial<CurrentUser>>(endpoints.me);
  return {
    userId: response.data.userId ?? 0,
    defaultTenantId: response.data.defaultTenantId ?? 0,
    hasPin: response.data.hasPin === true,
    name: response.data.name ?? '',
    username: response.data.username ?? '',
    preferences: response.data.preferences ?? DEFAULT_USER_DATE_PREFERENCES
  };
}

export async function updateCurrentUserPreferences(request: UpdateCurrentUserPreferencesRequest): Promise<void> {
  await axiosServices.patch(endpoints.currentUserPreferences, request);
}

export function useGetCurrentUser(tenantId: number | null, userId?: string | null) {
  const cacheKey = currentUserCacheKey(tenantId, userId);
  const { data, isLoading, error, isValidating } = useSWR<CurrentUser>(cacheKey, getCurrentUser, {
    revalidateIfStale: false,
    revalidateOnFocus: false,
    revalidateOnReconnect: false
  });

  return useMemo(
    () => ({
      currentUser: data ?? null,
      currentUserLoading: isLoading,
      currentUserError: error,
      currentUserValidating: isValidating
    }),
    [data, error, isLoading, isValidating]
  );
}

export function revalidateCurrentUser(tenantId: number | null, userId?: string | null) {
  const cacheKey = currentUserCacheKey(tenantId, userId);
  return cacheKey == null ? Promise.resolve(undefined) : mutate(cacheKey);
}

// Mirrors Weightrax.Contracts.Responses.CurrentUserPermissionsResponse. Resolved server-side
// for the selected tenant/company/site supplied in headers — no client-side JWT decoding involved.
export async function getCurrentUserPermissions(context?: {
  tenantId: number;
  companyId: number | null;
  siteId: number | null;
}): Promise<CurrentUserPermissions> {
  const response = await axiosServices.get<Partial<CurrentUserPermissions>>(
    endpoints.permissions,
    context
      ? {
          headers: {
            'X-Tenant-Id': String(context.tenantId),
            'X-Company-Id': context.companyId == null ? false : String(context.companyId),
            'X-Site-Id': context.siteId == null ? false : String(context.siteId)
          }
        }
      : undefined
  );
  return {
    roleName: response.data?.roleName ?? null,
    roleLevel: response.data?.roleLevel ?? null,
    roleNames: Array.isArray(response.data?.roleNames) ? response.data.roleNames : [],
    permissionCodes: Array.isArray(response.data?.permissionCodes) ? response.data.permissionCodes : []
  };
}

export interface TenantUser {
  userId: number;
  name: string;
  username: string;
  isActive: boolean;
  hasPin: boolean;
  roleId: number | null;
  roleName: string | null;
  roleLevel: number | null;
}

export interface CreateTenantUserRequest {
  name: string;
  username: string;
  roleId: number | null;
  pin?: string;
}

export interface UpdateTenantUserRequest {
  name: string;
  username: string;
}

export interface UserResponse {
  userId: number;
  username: string;
  name: string;
}

export interface RemoveTenantUserAccessResponse {
  requiresDeactivationConfirmation: boolean;
}

export interface TenantAccess {
  tenantId: number;
  tenantName: string;
}

export interface UserTenantAccess {
  userId: number;
  defaultTenantId: number;
  tenants: TenantAccess[];
}

// Mirrors Weightrax.Contracts.Responses.TenantUserResponse. Scoped to the tenant supplied via
// the X-Tenant-Id header.
export async function searchUsers(searchText?: string, isActive?: boolean): Promise<TenantUser[]> {
  const query = new URLSearchParams();
  if (searchText) query.set('searchText', searchText);
  if (isActive !== undefined) query.set('isActive', String(isActive));
  const suffix = query.toString();
  const url = suffix ? `${endpoints.search}?${suffix}` : endpoints.search;
  const response = await axiosServices.get<TenantUser[]>(url);
  return Array.isArray(response.data) ? response.data : [];
}

// Mirrors Weightrax.Contracts.Responses.UserDirectoryEntryResponse. Unlike searchUsers this reaches
// across every tenant, and the API gates it on manage:application accordingly.
export interface UserDirectoryEntry {
  userId: number;
  name: string;
  username: string;
  isActive: boolean;
  /** They already have access to the current tenant, so there is nothing to create for them. */
  isInCurrentTenant: boolean;
  /** The tenants they belong to besides the current one, alphabetically. */
  otherTenants: string[];
}

// Backs the type-ahead on the Name field when adding a user: finding a person who already has an
// account somewhere avoids giving them a second set of credentials and a split history.
export async function searchUserDirectory(searchText?: string): Promise<UserDirectoryEntry[]> {
  const query = new URLSearchParams();
  if (searchText) query.set('searchText', searchText);
  const suffix = query.toString();
  const url = suffix ? `${endpoints.directory}?${suffix}` : endpoints.directory;
  const response = await axiosServices.get<UserDirectoryEntry[]>(url);
  return Array.isArray(response.data)
    ? response.data.map((entry) => ({ ...entry, otherTenants: Array.isArray(entry.otherTenants) ? entry.otherTenants : [] }))
    : [];
}

// A pin sent alongside a client app role is set in the same call; the API rejects such a role for a
// user with no PIN unless one is supplied.
export async function setUserRole(userId: number, roleId: number | null, pin?: string): Promise<void> {
  await axiosServices.put(`api/users/${userId}/role`, { roleId, pin });
}

// Mirrors Weightrax.Contracts.Responses.UserTenantAccessResponse. Deliberately not tenant-scoped:
// the default has to be chosen from every tenant the user can reach, not just the current one.
export async function getUserTenantAccess(userId: number): Promise<UserTenantAccess> {
  const response = await axiosServices.get<Partial<UserTenantAccess>>(`api/users/${userId}/tenant-access`);
  return {
    userId: response.data?.userId ?? userId,
    defaultTenantId: response.data?.defaultTenantId ?? 0,
    tenants: Array.isArray(response.data?.tenants) ? response.data.tenants : []
  };
}

export async function setUserDefaultTenant(userId: number, defaultTenantId: number): Promise<void> {
  await axiosServices.put(`api/users/${userId}/default-tenant`, { defaultTenantId });
}

// Admin override — sets a new PIN without requiring or exposing the previous one.
export async function setUserPin(userId: number, pin: string): Promise<void> {
  await axiosServices.put(`api/users/${userId}/pin`, { pin });
}

export async function createUser(request: CreateTenantUserRequest): Promise<UserResponse> {
  const response = await axiosServices.post<UserResponse>('api/users', request);
  return response.data;
}

export async function updateUser(userId: number, request: UpdateTenantUserRequest): Promise<UserResponse> {
  const response = await axiosServices.put<UserResponse>(`api/users/${userId}`, request);
  return response.data;
}

export async function removeCurrentTenantUserAccess(userId: number): Promise<RemoveTenantUserAccessResponse> {
  const response = await axiosServices.delete<RemoveTenantUserAccessResponse>(`api/users/${userId}/tenant-access`);
  return response.data;
}

export async function deactivateOrphanedUser(userId: number): Promise<void> {
  await axiosServices.post(`api/users/${userId}/deactivate-orphaned`);
}

export async function reactivateUser(userId: number): Promise<void> {
  await axiosServices.post(`api/users/${userId}/reactivate`);
}

// ==============================|| ROLE ASSIGNMENTS ||============================== //
// Roles run from most specific to least: site (UserSite) → company (UserCompany) → tenant
// (UserTenant). A user holds one tenant role but may hold several roles at each company and site.

// Mirrors Weightrax.Domain.Enums.RoleScopeEnum.
export enum RoleScope {
  None = 0,
  Tenant = 1,
  Company = 2,
  Site = 3
}

export interface AssignedRole {
  roleId: number;
  roleName: string;
  roleLevel: number;
  roleType: RoleType;
}

export interface UserCompanyRoles {
  companyId: number;
  companyName: string;
  roles: AssignedRole[];
}

export interface UserSiteRoles {
  siteId: number;
  siteName: string;
  roles: AssignedRole[];
}

export interface UserRoleAssignments {
  userId: number;
  tenantId: number;
  tenantRole: AssignedRole | null;
  companies: UserCompanyRoles[];
  sites: UserSiteRoles[];
}

export interface EffectiveRoles {
  userId: number;
  tenantId: number;
  companyId: number | null;
  siteId: number | null;
  scope: RoleScope;
  roles: AssignedRole[];
  permissionCodes: string[];
}

export async function getUserRoleAssignments(userId: number): Promise<UserRoleAssignments> {
  const response = await axiosServices.get<UserRoleAssignments>(`api/users/${userId}/role-assignments`);
  return {
    userId: response.data?.userId ?? userId,
    tenantId: response.data?.tenantId ?? 0,
    tenantRole: response.data?.tenantRole ?? null,
    companies: Array.isArray(response.data?.companies) ? response.data.companies : [],
    sites: Array.isArray(response.data?.sites) ? response.data.sites : []
  };
}

// The roles that actually apply for a context — the most specific level at which the user holds any.
export async function getUserEffectiveRoles(userId: number, companyId?: number | null, siteId?: number | null): Promise<EffectiveRoles> {
  const query = new URLSearchParams();
  if (companyId != null) query.set('companyId', String(companyId));
  if (siteId != null) query.set('siteId', String(siteId));
  const suffix = query.toString();
  const url = `api/users/${userId}/effective-roles${suffix ? `?${suffix}` : ''}`;
  const response = await axiosServices.get<EffectiveRoles>(url);
  return response.data;
}

// An empty roleIds list attaches the user to the company without a company-level role.
export async function setUserCompanyRoles(userId: number, companyId: number, roleIds: number[], pin?: string): Promise<void> {
  await axiosServices.put(`api/users/${userId}/companies/${companyId}/roles`, { roleIds, pin });
}

export async function removeUserCompany(userId: number, companyId: number): Promise<void> {
  await axiosServices.delete(`api/users/${userId}/companies/${companyId}`);
}

// An empty roleIds list attaches the user to the site without a site-level role.
export async function setUserSiteRoles(userId: number, siteId: number, roleIds: number[], pin?: string): Promise<void> {
  await axiosServices.put(`api/users/${userId}/sites/${siteId}/roles`, { roleIds, pin });
}

export async function removeUserSite(userId: number, siteId: number): Promise<void> {
  await axiosServices.delete(`api/users/${userId}/sites/${siteId}`);
}
